import React, { useState, useEffect, useRef } from 'react';
import { FiPlus, FiTrash2, FiX, FiSearch } from 'react-icons/fi';
import { productosService, formulacionesService } from '../../services';
import Autocomplete from '../UI/Autocomplete';
import { safeParseFloat } from '../../utils';

/**
 * Componente de formulación según diseño Betel_1920x1080_maqueta_FORMULAS-FORMULARIO.pdf
 */
const FormulacionFormSimple = ({ onFormulacionChange, initialData = null, productName = '', productCategory = '', validationErrors = {} }) => {
  // Usar ref para callbacks que no deben causar re-renders
  const onFormulacionChangeRef = useRef(onFormulacionChange);
  const isInitializedRef = useRef(false);
  const lastNotifiedDataRef = useRef(null);
  const productNameInitializedRef = useRef(false);
  const lastLoadedInitialDataRef = useRef(null);
  const isLoadingInitialDataRef = useRef(false); // Flag para indicar que estamos cargando datos iniciales
  const currentFormDataRef = useRef(null); // Ref para comparar con initialData sin agregar formData como dep

  // Mantener la ref actualizada
  useEffect(() => {
    onFormulacionChangeRef.current = onFormulacionChange;
  }, [onFormulacionChange]);
  const [formData, setFormData] = useState({
    // Campos superiores
    company_name: 'BETEL NATURAL',
    formula_type: 'BETEL',
    formula_name: '',
    product_name: productName || '',
    container_product_id: '',  // Ahora guardamos ID en lugar de nombre
    lid_product_id: '',  // Ahora guardamos ID en lugar de nombre
    quantity_per_container: '90',
    quantity_unit: 'UNIT',
    capsule_type: 'VEGETARIAN',
    capsule_ingredient_id: '',
    mixing_time_minutes: 20,
    labor_cost_per_hour: '',
    best_use_date_years: '',
    label_printer: 'EN_BETEL',
    label_product_id: '',
    box_size: '16X13X13',
    pieces_per_box: '105',
    includes_silica: true,

    // Ingredientes
    ingredients: []
  });

  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [loadingIngredients, setLoadingIngredients] = useState(true);
  const [ingredientSearchResults, setIngredientSearchResults] = useState([]);
  const [searchingIngredients, setSearchingIngredients] = useState(false);
  const ingredientSearchDebounceRef = useRef(null);

  // Estado para el panel de agregar ingrediente
  const [addPanel, setAddPanel] = useState({
    searchText: '',
    selectedIngredient: null,
    quantity: '',
    unit: 'MG',
  });
  const searchInputRef = useRef(null);

  // Estados para cantidades de producción editables
  const [productionQuantities, setProductionQuantities] = useState([250, 500, 1000, 2000]);

  // Estados para material de empaque
  const [packagingMaterials, setPackagingMaterials] = useState([]);
  const [loadingPackaging, setLoadingPackaging] = useState(true);

  // Estado para generar estimado
  const [generatingEstimate, setGeneratingEstimate] = useState(false);

  // Opciones según el PDF
  const companyOptions = [
    { value: 'BETEL NATURAL', label: 'BETEL NATURAL' },
    { value: 'NATURALAND LAB', label: 'NATURALAND LAB' },
    { value: 'COTIZACIONES', label: 'COTIZACIONES' }
  ];

  const formulaTypeOptions = [
    { value: 'BETEL', label: 'BETEL' },
    { value: 'EXCLUSIVA', label: 'EXCLUSIVA' }
  ];

  // Productos de material de empaque - mostrar todos sin filtrar
  // Los productos ya vienen filtrados por product_type = 'PACKAGING_MATERIAL'

  const capsuleTypes = [
    { value: 'VEGETARIAN', label: 'VEGETARIANA' },
    { value: 'GELATIN', label: 'GELATINA' },
    { value: 'NOT_APPLICABLE', label: 'NO APLICA' }
  ];

  const labelPrinterOptions = [
    { value: 'EN_BETEL', label: 'EN BETEL' },
    { value: 'EXTERNAL', label: 'EXTERNA' },
    { value: 'NO_LABEL', label: 'SIN ETIQUETA' }
  ];

  const boxSizeOptions = [
    { value: '16X13X13', label: '16X13X13' },
    { value: '18X15X15', label: '18X15X15' },
    { value: '20X15X15', label: '20X15X15' },
    { value: '22X18X18', label: '22X18X18' }
  ];

  const unitTypes = [
    // Peso
    { value: 'KG',  label: 'kg'  },
    { value: 'G',   label: 'g'   },
    { value: 'MG',  label: 'mg'  },
    { value: 'MCG', label: 'mcg' },
    // Volumen
    { value: 'L',   label: 'l'   },
    { value: 'ML',  label: 'ml'  },
    // Conteo
    { value: 'UNIT', label: 'unid' },
  ];

  const quantityUnitOptions = [
    { value: 'UNIT', label: 'UNIDADES' },
    { value: 'MG', label: 'MG' },
    { value: 'G', label: 'G' },
    { value: 'KG', label: 'KG' },
    { value: 'ML', label: 'ML' },
    { value: 'L', label: 'L' }
  ];

  const quantityTypeOptions = [
    { value: 'CAPSULA', label: 'CÁPSULA' },
    { value: 'INFUSION', label: 'INFUSIÓN' },
    { value: 'ENVASE', label: 'ENVASE' }
  ];

  // Determinar si la unidad del envase es discreta (UNIT) o continua (peso/volumen)
  // UNIT: ingredientes son "por unidad individual" (cápsula, bolsita), qpc multiplica
  // Peso/Volumen: ingredientes son "por envase completo", qpc es informativo (multiplicador = 1)
  const isDiscreteUnit = () => formData.quantity_unit === 'UNIT';

  // Obtener el multiplicador efectivo para cálculos de ingredientes
  const getEffectiveQpc = () => {
    if (isDiscreteUnit()) {
      return safeParseFloat(formData.quantity_per_container, 1);
    }
    // Para peso/volumen, el ingrediente ya es por envase completo
    return 1;
  };

  // Obtener label de la unidad del envase para mostrar en la tabla
  const getQpcLabel = () => {
    const qpc = formData.quantity_per_container || 0;
    const unit = formData.quantity_unit || 'UNIT';
    if (unit === 'UNIT') return `${qpc} unid/envase`;
    return `${qpc} ${unit.toLowerCase()}/envase`;
  };

  // Helper para obtener nombre del producto de empaque por ID
  const getPackagingMaterialName = (productId) => {
    if (!productId) return 'Sin envase';
    // Convertir a número para comparar
    const numericId = typeof productId === 'string' ? parseInt(productId) : productId;
    const product = packagingMaterials.find(p => p.id === numericId);
    return product ? product.name : 'Sin envase';
  };

  // Helper para obtener nombre de categoría en español
  const getCategoryDisplayName = (category) => {
    const categoryMap = {
      'CAPSULES': 'CÁPSULA',
      'POWDERS': 'POLVO',
      'OILS': 'ACEITE',
      'TEAS': 'TÉ',
      'LEAVES': 'HOJA',
      'LIQUIDS': 'LÍQUIDO',
      'CREAMS': 'CREMA',
      'SUPPLEMENTS': 'SUPLEMENTO',
      'TABLETS': 'TABLETA',
      'EXTRACTS': 'EXTRACTO',
      'HERBS': 'HIERBA',
      'VITAMINS': 'VITAMINA',
      'MINERALS': 'MINERAL',
      'SUPPLIES': 'INSUMO',
      'PACKAGING': 'EMPAQUE',
      'OTHER': 'OTRO'
    };
    return categoryMap[category] || category;
  };

  // Mantener ref de formData actualizada (para comparación en useEffect de initialData)
  useEffect(() => {
    currentFormDataRef.current = formData;
  }, [formData]);

  // Cargar ingredientes y material de empaque disponibles
  useEffect(() => {
    loadIngredients();
    loadPackagingMaterials();
  }, []);


  // Notificar cambios al componente padre
  useEffect(() => {
    // NO notificar si estamos cargando datos iniciales
    if (isLoadingInitialDataRef.current) {
      console.log('⏭️ Saltando notificación - cargando datos iniciales');
      return;
    }

    // Solo notificar después de la inicialización
    if (isInitializedRef.current && onFormulacionChangeRef.current) {
      // Filtrar ingredientes temporales (isNew) antes de notificar
      const dataToNotify = {
        ...formData,
        ingredients: formData.ingredients.filter(ing => !ing.isNew),
        computed_cost_price: null  // se calcula abajo
      };

      // Calcular costo total por envase (ingredientes + mano de obra + empaque)
      {
        let totalCost = 0;
        const effectiveQpc = getEffectiveQpc();
        // Costo de ingredientes
        dataToNotify.ingredients.forEach(ing => {
          const qtyPerUnit = safeParseFloat(ing.quantity_per_unit);
          const costPrice = safeParseFloat(ing.cost_price);
          if (costPrice > 0) {
            const baseUnit = ing.base_unit || ing.unit;
            const formulaUnit = ing.unit;
            const factorBase = UNIT_TO_BASE[baseUnit] || 1;
            const factorFormula = UNIT_TO_BASE[formulaUnit] || 1;
            const costPerFormulaUnit = costPrice * (factorFormula / factorBase);
            totalCost += costPerFormulaUnit * qtyPerUnit * effectiveQpc;
          } else {
            const costPerUnit = safeParseFloat(ing.cost_per_unit);
            if (costPerUnit > 0) totalCost += costPerUnit * qtyPerUnit * effectiveQpc;
          }
        });
        // Mano de obra (valor directo ingresado por el usuario)
        const laborCost = safeParseFloat(formData.labor_cost_per_hour);
        if (laborCost > 0) {
          totalCost += laborCost;
        }
        // Costo de envase y tapadera
        if (formData.container_product_id) {
          const container = packagingMaterials.find(p => p.id === Number(formData.container_product_id));
          if (container) totalCost += safeParseFloat(container.cost_price);
        }
        if (formData.lid_product_id) {
          const lid = packagingMaterials.find(p => p.id === Number(formData.lid_product_id));
          if (lid) totalCost += safeParseFloat(lid.cost_price);
        }
        // Costo ingrediente cápsula (solo aplica para unidades discretas)
        if (formData.capsule_type !== 'NOT_APPLICABLE' && formData.capsule_ingredient_id) {
          const capsuleIng = availableIngredients.find(p => p.id === Number(formData.capsule_ingredient_id));
          if (capsuleIng) {
            totalCost += safeParseFloat(capsuleIng.cost_price) * effectiveQpc;
          }
        }
        // Costo etiqueta
        if (formData.label_printer !== 'NO_LABEL' && formData.label_product_id) {
          const labelMat = packagingMaterials.find(p => p.id === Number(formData.label_product_id));
          if (labelMat) totalCost += safeParseFloat(labelMat.cost_price);
        }
        dataToNotify.computed_cost_price = Math.round(totalCost * 100) / 100;
      }

      // Comparar con la última notificación para evitar bucles
      const currentDataString = JSON.stringify(dataToNotify);
      if (currentDataString !== lastNotifiedDataRef.current) {
        lastNotifiedDataRef.current = currentDataString;
        console.log('📤 Notificando cambios al componente padre:', dataToNotify);
        console.log('📊 Ingredientes confirmados:', dataToNotify.ingredients);
        onFormulacionChangeRef.current(dataToNotify);
      }
    }
  }, [formData, packagingMaterials, availableIngredients]);

  // Cargar datos iniciales cuando initialData cambia (especialmente importante para modo edición)
  useEffect(() => {
    // Si no hay initialData, marcar como inicializado para permitir notificaciones
    if (!initialData || Object.keys(initialData).length === 0) {
      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
        console.log('✅ Inicializado sin datos iniciales - notificaciones habilitadas');
      }
      return;
    }

    // Solo cargar si initialData tiene contenido significativo
    if (initialData && Object.keys(initialData).length > 0) {
      // Verificar si los datos realmente cambiaron comparando con la última carga
      const initialDataString = JSON.stringify(initialData);
      if (initialDataString === lastLoadedInitialDataRef.current) {
        console.log('⏭️ initialData no ha cambiado, saltando actualización');
        return;
      }

      // Si ya estamos inicializados, detectar si este cambio es solo nuestro propio
      // dato rebotando desde el padre (loop circular) — si los campos clave coinciden
      // con el formData actual, no re-inicializar para no perder el estado del usuario
      if (isInitializedRef.current && currentFormDataRef.current) {
        const cur = currentFormDataRef.current;
        const keyFields = [
          'container_product_id', 'lid_product_id', 'capsule_ingredient_id',
          'label_product_id', 'capsule_type', 'label_printer', 'formula_name',
          'quantity_per_container', 'mixing_time_minutes', 'labor_cost_per_hour',
          'formula_id', 'company_name', 'formula_type'
        ];
        const fieldsMatch = keyFields.every(
          k => String(initialData[k] ?? '') === String(cur[k] ?? '')
        );
        const curIngIds = (cur.ingredients || []).filter(i => !i.isNew)
          .map(i => i.ingredient_id).sort().join(',');
        const initIngIds = (initialData.ingredients || [])
          .map(i => i.ingredient_id).sort().join(',');

        if (fieldsMatch && curIngIds === initIngIds) {
          // Es un bounce-back de nuestra propia notificación — solo actualizar el ref
          lastLoadedInitialDataRef.current = initialDataString;
          console.log('⏭️ Bounce-back detectado, saltando re-inicialización');
          return;
        }
      }

      // ACTIVAR FLAG: estamos cargando datos iniciales
      isLoadingInitialDataRef.current = true;
      console.log('🔄 FormulacionFormSimple recibió initialData:', initialData);

      // Sanitizar datos para convertir valores antiguos a los nuevos
      const sanitizedData = {
        ...initialData,
        // Convertir VEGETAL a VEGETARIAN (valor antiguo a nuevo)
        capsule_type: initialData.capsule_type === 'VEGETAL' ? 'VEGETARIAN' : (initialData.capsule_type || 'VEGETARIAN'),
        // Convertir CAPS a UNIT si existe (valor antiguo a nuevo)
        quantity_unit: initialData.quantity_unit === 'CAPS' ? 'UNIT' : (initialData.quantity_unit || 'UNIT'),

        // IDs pueden ser 0, usar verificación explícita
        container_product_id: initialData.container_product_id !== null && initialData.container_product_id !== undefined
          ? initialData.container_product_id
          : '',
        lid_product_id: initialData.lid_product_id !== null && initialData.lid_product_id !== undefined
          ? initialData.lid_product_id
          : '',

        // Strings vacíos por defecto
        formula_name: initialData.formula_name || '',
        company_name: initialData.company_name || 'BETEL NATURAL',
        formula_type: initialData.formula_type || 'BETEL',
        product_name: initialData.product_name || '',

        // Números con valores por defecto
        quantity_per_container: initialData.quantity_per_container ?? 90,
        mixing_time_minutes: initialData.mixing_time_minutes ?? 20,
        labor_cost_per_hour: initialData.labor_cost_per_hour ?? '',
        pieces_per_box: initialData.pieces_per_box ?? 105,

        // Fecha de mejor uso: guardada como fecha ISO, mostrada como meses
        best_use_date_years: initialData.best_use_date_years || '',
        _best_use_months: (() => {
          if (!initialData.best_use_date_years) return '';
          const saved = new Date(initialData.best_use_date_years + 'T00:00:00');
          if (isNaN(saved.getTime())) return '';
          const now = new Date();
          const diffMonths = (saved.getFullYear() - now.getFullYear()) * 12 + (saved.getMonth() - now.getMonth());
          return diffMonths > 0 ? diffMonths : '';
        })(),
        label_printer: initialData.label_printer || 'EN_BETEL',
        label_product_id: initialData.label_product_id !== null && initialData.label_product_id !== undefined
          ? initialData.label_product_id
          : '',
        box_size: initialData.box_size || '16X13X13',

        // Ingrediente cápsula
        capsule_ingredient_id: initialData.capsule_ingredient_id !== null && initialData.capsule_ingredient_id !== undefined
          ? initialData.capsule_ingredient_id
          : '',

        // Boolean debe verificarse explícitamente
        includes_silica: initialData.includes_silica === true,

        // Ingredientes (array) — preservar base_unit si ya existe en cada ingrediente
        ingredients: (initialData.ingredients || []).map(ing => ({
          ...ing,
          base_unit: ing.base_unit || ing.unit || 'MG',
        }))
      };

      console.log('✅ Datos sanitizados que se cargarán en el formulario:', sanitizedData);
      setFormData(prev => {
        const newData = { ...prev, ...sanitizedData };
        console.log('🔄 Estado actualizado - formData ahora es:', newData);
        return newData;
      });
      lastLoadedInitialDataRef.current = initialDataString;
      isInitializedRef.current = true;

      // DESACTIVAR FLAG después de que React procese la actualización de estado
      setTimeout(() => {
        isLoadingInitialDataRef.current = false;
        console.log('✅ Carga de datos iniciales completada - notificaciones habilitadas');
      }, 0);
    }
  }, [initialData]); // Ejecutar cuando initialData cambie

  // Debug: ver el estado actual de formData (solo cuando cambian los valores clave)
  useEffect(() => {
    if (formData.formula_name || formData.container_product_id || formData.lid_product_id) {
      console.log('📊 Estado formData - campos clave:', {
        formula_name: formData.formula_name,
        container_product_id: formData.container_product_id,
        lid_product_id: formData.lid_product_id,
        ingredients: formData.ingredients?.length
      });
    }
  }, [formData.formula_name, formData.container_product_id, formData.lid_product_id]);

  // Actualizar product_name cuando cambia el prop
  useEffect(() => {
    if (productName && !productNameInitializedRef.current) {
      setFormData(prev => ({
        ...prev,
        product_name: productName,
        formula_name: prev.formula_name || `${productName} Formula`,
      }));
      productNameInitializedRef.current = true;
    }
  }, [productName]);

  // Debounced ingredient search
  useEffect(() => {
    if (addPanel.selectedIngredient) return;
    if (ingredientSearchDebounceRef.current) clearTimeout(ingredientSearchDebounceRef.current);
    if (!addPanel.searchText) {
      setIngredientSearchResults([]);
      return;
    }
    ingredientSearchDebounceRef.current = setTimeout(() => {
      handleSearchIngredients(addPanel.searchText);
    }, 300);
    return () => clearTimeout(ingredientSearchDebounceRef.current);
  }, [addPanel.searchText, addPanel.selectedIngredient]); // eslint-disable-line

  const loadIngredients = async () => {
    setLoadingIngredients(true);
    try {
      const result = await productosService.getProductos({
        product_type: 'INGREDIENT',
        limit: 500
      });

      if (result.success && result.data) {
        const ingredientes = result.data.productos || result.data || [];
        setAvailableIngredients(ingredientes);
      }
    } catch (error) {
      console.error('Error loading ingredients:', error);
    } finally {
      setLoadingIngredients(false);
    }
  };

  const handleSearchIngredients = async (searchTerm) => {
    if (!searchTerm) {
      setIngredientSearchResults([]);
      return;
    }
    const lowerTerm = searchTerm.toLowerCase();
    if (searchTerm.length < 3) {
      const local = availableIngredients.filter(i =>
        i.name.toLowerCase().includes(lowerTerm) || (i.code || '').toLowerCase().includes(lowerTerm)
      ).slice(0, 10);
      setIngredientSearchResults(local);
      return;
    }
    setSearchingIngredients(true);
    try {
      const result = await productosService.getProductos({
        product_type: 'INGREDIENT',
        search: searchTerm,
        limit: 50,
      });
      if (result.success && result.data) {
        const items = result.data.productos || result.data || [];
        setIngredientSearchResults(items.slice(0, 20));
      }
    } catch (error) {
      console.error('Error searching ingredients:', error);
      const local = availableIngredients.filter(i =>
        i.name.toLowerCase().includes(lowerTerm) || (i.code || '').toLowerCase().includes(lowerTerm)
      ).slice(0, 10);
      setIngredientSearchResults(local);
    } finally {
      setSearchingIngredients(false);
    }
  };

  const loadPackagingMaterials = async () => {
    setLoadingPackaging(true);
    try {
      const result = await productosService.getProductos({
        product_type: 'PACKAGING_MATERIAL',
        limit: 500,
        status: 'ACTIVE'
      });
      if (result.success && result.data) {
        const materials = result.data.productos || result.data || [];
        setPackagingMaterials(materials);
      }
    } catch (error) {
      console.error('Error loading packaging materials:', error);
    } finally {
      setLoadingPackaging(false);
    }
  };

  const handleSearchPackaging = async (searchTerm) => {
    // Sin texto o menos de 3 chars: filtrar del caché local
    if (!searchTerm || searchTerm.length < 3) {
      // Sin texto: devolver TODOS (para que la resolución por ID funcione siempre)
      if (!searchTerm) return packagingMaterials;
      const lowerTerm = searchTerm.toLowerCase();
      return packagingMaterials.filter(item =>
        (item.name || '').toLowerCase().includes(lowerTerm) ||
        (item.code || '').toLowerCase().includes(lowerTerm)
      ).slice(0, 50);
    }
    // 3+ caracteres: búsqueda real-time al API
    try {
      const result = await productosService.getProductos({
        product_type: 'PACKAGING_MATERIAL',
        status: 'ACTIVE',
        search: searchTerm,
        limit: 50,
      });
      if (result.success && result.data) {
        const items = result.data.productos || result.data || [];
        // Merge al caché para que getPackagingCost() encuentre el item seleccionado
        setPackagingMaterials(prev => {
          const newItems = items.filter(item => !prev.find(p => p.id === item.id));
          return newItems.length > 0 ? [...prev, ...newItems] : prev;
        });
        return items;
      }
    } catch (error) {
      console.error('Error searching packaging:', error);
    }
    return [];
  };

  const handleSearchCapsuleIngredient = async (searchTerm) => {
    if (!searchTerm) return availableIngredients;
    const lowerTerm = searchTerm.toLowerCase();
    if (searchTerm.length < 3) {
      return availableIngredients.filter(item =>
        (item.name || '').toLowerCase().includes(lowerTerm) ||
        (item.code || '').toLowerCase().includes(lowerTerm)
      ).slice(0, 50);
    }
    try {
      const result = await productosService.getProductos({
        product_type: 'INGREDIENT',
        search: searchTerm,
        limit: 50,
      });
      if (result.success && result.data) {
        const items = result.data.productos || result.data || [];
        // Merge al caché para que getPackagingCost() encuentre el item seleccionado
        setAvailableIngredients(prev => {
          const newItems = items.filter(item => !prev.find(p => p.id === item.id));
          return newItems.length > 0 ? [...prev, ...newItems] : prev;
        });
        return items;
      }
    } catch (error) {
      console.error('Error searching capsule ingredient:', error);
    }
    return availableIngredients.filter(item =>
      (item.name || '').toLowerCase().includes(lowerTerm)
    ).slice(0, 20);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      // Si cambia el tipo de fórmula a EXCLUSIVA, limpiar el nombre
      if (field === 'formula_type' && value === 'EXCLUSIVA') {
        newData.formula_name = '';
      }

      // Si cambia tipo de cápsula a NO APLICA, limpiar el ingrediente seleccionado
      if (field === 'capsule_type' && value === 'NOT_APPLICABLE') {
        newData.capsule_ingredient_id = '';
      }

      // Si cambia etiqueta a SIN ETIQUETA, limpiar el material seleccionado
      if (field === 'label_printer' && value === 'NO_LABEL') {
        newData.label_product_id = '';
      }

      return newData;
    });
  };

  const handleAddPanelClose = () => {
    setAddPanel({ searchText: '', selectedIngredient: null, quantity: '', unit: 'MG' });
    setIngredientSearchResults([]);
  };

  const handleAddPanelSelectIngredient = (ingredient) => {
    const rawUnit = ingredient.unit;
    const unitMap = {
      KILOGRAM: 'KG', GRAM: 'G', MILLIGRAM: 'MG', MICROGRAM: 'MCG',
      LITER: 'L', MILLILITER: 'ML', METER: 'M', CENTIMETER: 'CM',
      UNIT: 'UNIT', BOTTLE: 'BOTTLE', BOX: 'BOX', PACKAGE: 'PACKAGE',
    };
    const normalizedUnit = rawUnit ? (unitMap[rawUnit] || rawUnit) : 'MG';
    setIngredientSearchResults([]);
    setAddPanel(prev => ({
      ...prev,
      searchText: ingredient.name,
      selectedIngredient: ingredient,
      unit: normalizedUnit,
    }));
  };

  const handleAddPanelConfirm = () => {
    if (!addPanel.selectedIngredient) {
      alert('Seleccione un ingrediente de la lista');
      return;
    }
    if (!addPanel.quantity || parseFloat(addPanel.quantity) <= 0) {
      alert('Ingrese una cantidad válida mayor a 0');
      return;
    }

    const rawUnit = addPanel.selectedIngredient.unit;
    const unitMap = {
      KILOGRAM: 'KG', GRAM: 'G', MILLIGRAM: 'MG', MICROGRAM: 'MCG',
      LITER: 'L', MILLILITER: 'ML', METER: 'M', CENTIMETER: 'CM',
      UNIT: 'UNIT', BOTTLE: 'BOTTLE', BOX: 'BOX', PACKAGE: 'PACKAGE',
    };
    const baseUnit = rawUnit ? (unitMap[rawUnit] || rawUnit) : 'MG';

    const newIngredient = {
      ingredient_id: addPanel.selectedIngredient.id,
      ingredient_name: addPanel.selectedIngredient.name,
      quantity_per_unit: parseFloat(addPanel.quantity),
      unit: addPanel.unit,
      base_unit: baseUnit,
      cost_price: parseFloat(addPanel.selectedIngredient.cost_price) || 0,
    };

    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, newIngredient]
    }));
    handleAddPanelClose();
  };

  const handleRemoveIngredient = (displayIndex) => {
    // displayIndex is the index within the filtered (confirmed) list
    const confirmed = formData.ingredients.filter(i => !i.isNew);
    const ingredientToRemove = confirmed[displayIndex];
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter(i => i !== ingredientToRemove)
    }));
  };

  const handleIngredientChange = (displayIndex, field, value) => {
    // displayIndex is within the filtered (confirmed) list
    const confirmed = formData.ingredients.filter(i => !i.isNew);
    const targetIngredient = confirmed[displayIndex];

    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing) => {
        if (ing !== targetIngredient) return ing;

          // Si es un campo numérico, parsearlo; si no, usar el valor directo
          const newValue = (field === 'quantity_per_unit')
            ? (parseFloat(value) || 0)
            : value;
          return { ...ing, [field]: newValue };
      })
    }));
  };

  // Calcular total por unidad (incluye todos los ingredientes con cantidad)
  const getTotalPerUnit = () => {
    // Convertir todo a gramos (para peso) o mililitros (para volumen)
    let totalGrams = 0;
    let totalML = 0;

    formData.ingredients.forEach(ing => {
      const amount = parseFloat(ing.quantity_per_unit) || 0;
      const unit = ing.unit;

      // Convertir unidades de peso a gramos
      if (unit === 'KG') {
        totalGrams += amount * 1000;
      } else if (unit === 'G') {
        totalGrams += amount;
      } else if (unit === 'MG') {
        totalGrams += amount / 1000;
      } else if (unit === 'MCG') {
        totalGrams += amount / 1000000;
      }
      // Convertir unidades de volumen a mililitros
      else if (unit === 'L') {
        totalML += amount * 1000;
      } else if (unit === 'ML') {
        totalML += amount;
      }
    });

    // Retornar objeto con ambos totales
    return { grams: totalGrams, ml: totalML };
  };

  // Formatear número: separador de miles con coma, sin ceros decimales innecesarios
  const formatNum = (num, maxDecimals = 2) => {
    // Redondear al número de decimales deseado
    const rounded = parseFloat(num.toFixed(maxDecimals));
    // Usar toLocaleString para separar miles con coma
    return rounded.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxDecimals,
    });
  };

  // Formatear peso con auto-conversión a la unidad más legible
  const formatWeight = (grams) => {
    if (grams >= 1000) return `${formatNum(grams / 1000)} kg`;
    return `${formatNum(grams)} g`;
  };

  // Formatear volumen con auto-conversión a la unidad más legible
  const formatVolume = (ml) => {
    if (ml >= 1000) return `${formatNum(ml / 1000)} l`;
    return `${formatNum(ml)} ml`;
  };

  // Calcular cantidad total para producción (unidades por envase × envases a producir)
  const getProductionAmount = (quantity) => {
    const totals = getTotalPerUnit();
    const effectiveQpc = getEffectiveQpc();
    const totalGrams = totals.grams * effectiveQpc * quantity;
    const totalML = totals.ml * effectiveQpc * quantity;

    // Si hay peso y volumen, mostrar ambos en líneas separadas
    if (totalGrams > 0 && totalML > 0) {
      return (
        <div style={{ lineHeight: '1.4' }}>
          <div>{formatWeight(totalGrams)}</div>
          <div>{formatVolume(totalML)}</div>
        </div>
      );
    }
    if (totalGrams > 0) return formatWeight(totalGrams);
    if (totalML > 0) return formatVolume(totalML);
    return '-';
  };

  // Calcular cantidad por ingrediente para producción (por unidad × unidades por envase × envases)
  const getIngredientProductionAmount = (ingredient, quantity) => {
    const amountPerUnit = parseFloat(ingredient.quantity_per_unit) || 0;
    const unit = ingredient.unit;

    // Si no hay cantidad, mostrar '-'
    if (amountPerUnit === 0) {
      return '-';
    }

    // Calcular total: cantidad por unidad × multiplicador efectivo × envases a producir
    const effectiveQpc = getEffectiveQpc();
    const total = amountPerUnit * effectiveQpc * quantity;

    // Formatear según el tipo de unidad
    switch(unit) {
      case 'KG':
        return `${formatNum(total)} kg`;

      case 'G':
        return formatWeight(total);

      case 'MG': {
        const totalGramsMG = total / 1000;
        if (totalGramsMG >= 1000) return `${formatNum(totalGramsMG / 1000)} kg`;
        if (totalGramsMG >= 1) return `${formatNum(totalGramsMG)} g`;
        return `${formatNum(total)} mg`;
      }

      case 'MCG': {
        const totalMgMCG = total / 1000;
        const totalGramsMCG = total / 1000000;
        if (totalGramsMCG >= 1000) return `${formatNum(totalGramsMCG / 1000)} kg`;
        if (totalGramsMCG >= 1) return `${formatNum(totalGramsMCG)} g`;
        if (totalMgMCG >= 1) return `${formatNum(totalMgMCG)} mg`;
        return `${formatNum(total, 0)} mcg`;
      }

      case 'L':
        return `${formatNum(total)} l`;

      case 'ML':
        return formatVolume(total);

      case 'UNIT':
        return `${formatNum(total, 0)} unid`;

      default:
        return `${formatNum(total)} ${unit}`;
    }
  };

  // Factores de conversión a unidad base (peso→gramos, volumen→ml)
  const UNIT_TO_BASE = {
    KG: 1000, G: 1, MG: 0.001, MCG: 0.000001,
    L: 1000, ML: 1,
    UNIT: 1, BOTTLE: 1, BOX: 1, PACKAGE: 1,
  };

  // Calcular costo de un ingrediente para un envase completo
  const getIngredientCostPerContainer = (ingredient) => {
    const qtyPerUnit = safeParseFloat(ingredient.quantity_per_unit);
    const effectiveQpc = getEffectiveQpc();

    // Opción 1: usar cost_price del producto (precio de compra) y convertir unidades
    const costPrice = safeParseFloat(ingredient.cost_price);
    if (costPrice > 0) {
      const baseUnit = ingredient.base_unit || ingredient.unit;
      const formulaUnit = ingredient.unit;
      const factorBase = UNIT_TO_BASE[baseUnit] || 1;
      const factorFormula = UNIT_TO_BASE[formulaUnit] || 1;
      const costPerFormulaUnit = costPrice * (factorFormula / factorBase);
      return costPerFormulaUnit * qtyPerUnit * effectiveQpc;
    }

    // Opción 2: usar cost_per_unit ya convertido (de fórmulas existentes)
    const costPerUnit = safeParseFloat(ingredient.cost_per_unit);
    if (costPerUnit > 0) {
      return costPerUnit * qtyPerUnit * effectiveQpc;
    }

    return null;
  };

  // Calcular costo total de todos los ingredientes para un envase
  const getTotalIngredientCostPerContainer = () => {
    let total = 0;
    formData.ingredients.filter(i => !i.isNew).forEach(ing => {
      const cost = getIngredientCostPerContainer(ing);
      if (cost !== null) total += cost;
    });
    return total;
  };

  // Calcular costo de mano de obra por envase (valor directo ingresado por el usuario)
  const getLaborCostPerContainer = () => {
    return safeParseFloat(formData.labor_cost_per_hour);
  };

  // Costo de envase, tapadera y cápsula
  const getPackagingCost = () => {
    let cost = 0;
    if (formData.container_product_id) {
      const container = packagingMaterials.find(p => p.id === Number(formData.container_product_id));
      if (container) cost += safeParseFloat(container.cost_price);
    }
    if (formData.lid_product_id) {
      const lid = packagingMaterials.find(p => p.id === Number(formData.lid_product_id));
      if (lid) cost += safeParseFloat(lid.cost_price);
    }
    // Costo de ingrediente cápsula (cost_price × cantidad por envase, solo para unidades discretas)
    if (formData.capsule_type !== 'NOT_APPLICABLE' && formData.capsule_ingredient_id) {
      const capsuleIng = availableIngredients.find(p => p.id === Number(formData.capsule_ingredient_id));
      if (capsuleIng) {
        cost += safeParseFloat(capsuleIng.cost_price) * getEffectiveQpc();
      }
    }
    // Costo de etiqueta (material de empaque)
    if (formData.label_printer !== 'NO_LABEL' && formData.label_product_id) {
      const labelMat = packagingMaterials.find(p => p.id === Number(formData.label_product_id));
      if (labelMat) cost += safeParseFloat(labelMat.cost_price);
    }
    return cost;
  };

  // Calcular costo total de producción por envase (ingredientes + mano de obra + envase + tapadera)
  const getTotalCostPerContainer = () => {
    return getTotalIngredientCostPerContainer() + getLaborCostPerContainer() + getPackagingCost();
  };

  // Generar estimado de costos
  const handleGenerarEstimado = async () => {
    // Validaciones
    if (formData.ingredients.length === 0) {
      alert('Debes agregar al menos un ingrediente antes de generar el estimado');
      return;
    }

    if (!formData.container_product_id) {
      alert('Debes seleccionar un tipo de envase antes de generar el estimado');
      return;
    }

    // Verificar si tenemos formula_id (producto ya guardado)
    if (!initialData?.formula_id) {
      alert('Debes guardar el producto y su formulación primero antes de generar el estimado.\n\nHaz clic en "Crear Producto" o "Actualizar Producto" para guardar los cambios.');
      return;
    }

    setGeneratingEstimate(true);

    try {
      const formulaId = initialData.formula_id;
      console.log('📊 Generando estimado para formula_id:', formulaId);
      console.log('📊 Cantidades de producción:', productionQuantities);

      // Calcular para todas las cantidades de producción en paralelo
      const laborRate = parseFloat(formData.labor_cost_per_hour) || undefined;
      const calcularPromises = productionQuantities.map(quantity =>
        formulacionesService.calcularCostos(formulaId, {
          production_quantity: parseInt(quantity),
          include_labor_cost: true,
          labor_cost_per_hour: laborRate,
        })
      );

      const resultados = await Promise.all(calcularPromises);

      // Verificar que todos los resultados sean exitosos
      const todosExitosos = resultados.every(r => r.success);
      if (!todosExitosos) {
        throw new Error('Error al calcular costos para algunas cantidades');
      }

      console.log('✅ Cálculos de costos obtenidos:', resultados.map(r => r.data));

      // Generar tirilla HTML con todos los resultados
      generarTirillaEstimado(formData, resultados.map(r => r.data));

    } catch (error) {
      console.error('❌ Error al generar estimado:', error);
      alert('Error al generar el estimado: ' + (error.message || 'Error desconocido'));
    } finally {
      setGeneratingEstimate(false);
    }
  };

  const generarTirillaEstimado = (formula, calculos) => {
    const tirillaHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Estimado de Costos - ${formula.formula_name || 'Sin nombre'}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          body {
            font-family: 'Courier New', monospace;
            padding: 20px;
            max-width: 900px;
            margin: 0 auto;
            font-size: 12px;
            line-height: 1.4;
          }

          .header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px dashed #333;
            padding-bottom: 15px;
          }

          .logo {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 5px;
            color: #7B2D8E;
          }

          .title {
            font-size: 14px;
            font-weight: bold;
            margin: 10px 0;
            text-transform: uppercase;
          }

          .info-section {
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 1px dashed #999;
          }

          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 5px;
          }

          .label {
            font-weight: bold;
          }

          .value {
            text-align: right;
          }

          .cost-table {
            width: 100%;
            margin: 15px 0;
            border-collapse: collapse;
          }

          .cost-table th,
          .cost-table td {
            padding: 8px;
            text-align: center;
            border: 1px solid #ddd;
          }

          .cost-table th {
            background-color: #7B2D8E;
            color: white;
            font-weight: bold;
          }

          .cost-table .row-label {
            text-align: left;
            font-weight: bold;
            background-color: #f5f5f5;
          }

          .total-row {
            font-weight: bold;
            background-color: #f9f9f9;
          }

          .footer {
            margin-top: 20px;
            padding-top: 15px;
            border-top: 2px dashed #333;
            text-align: center;
            font-size: 10px;
            color: #666;
          }

          @media print {
            body {
              padding: 10px;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">BETEL NATURAL</div>
          <div class="title">Estimado de Costos - Comparativa</div>
          <div>${new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
        </div>

        <div class="info-section">
          <div class="info-row">
            <span class="label">Compañía:</span>
            <span class="value">${formula.company_name}</span>
          </div>
          <div class="info-row">
            <span class="label">Fórmula:</span>
            <span class="value">${formula.formula_name}</span>
          </div>
          <div class="info-row">
            <span class="label">Cantidad por Envase:</span>
            <span class="value">${formula.quantity_per_container} ${formula.quantity_unit}</span>
          </div>
        </div>

        <div class="info-section">
          <div class="title">Comparativa de Costos por Cantidad de Producción</div>
          <table class="cost-table">
            <thead>
              <tr>
                <th class="row-label">Concepto</th>
                ${calculos.map(c => `<th>${c.production_quantity} unidades</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="row-label">Costo de Ingredientes</td>
                ${calculos.map(c => `<td>$${c.total_ingredient_cost.toFixed(2)}</td>`).join('')}
              </tr>
              <tr>
                <td class="row-label">Costo de Empaque</td>
                ${calculos.map(c => `<td>$${(c.packaging_cost || 0).toFixed(2)}</td>`).join('')}
              </tr>
              <tr>
                <td class="row-label">Costo de Mano de Obra</td>
                ${calculos.map(c => `<td>$${(c.labor_cost || 0).toFixed(2)}</td>`).join('')}
              </tr>
              <tr>
                <td class="row-label">Costos Indirectos</td>
                ${calculos.map(c => `<td>$${(c.overhead_cost || 0).toFixed(2)}</td>`).join('')}
              </tr>
              <tr class="total-row">
                <td class="row-label">COSTO TOTAL</td>
                ${calculos.map(c => `<td>$${c.total_cost.toFixed(2)}</td>`).join('')}
              </tr>
              <tr class="total-row">
                <td class="row-label">COSTO POR UNIDAD</td>
                ${calculos.map(c => `<td>$${c.cost_per_unit.toFixed(2)}</td>`).join('')}
              </tr>
            </tbody>
          </table>
        </div>

        <div class="footer">
          <p>Estimado generado el ${new Date().toLocaleString('es-ES')}</p>
          <p>Sistema Klever Octopus - Betel Natural</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
      </html>
    `;

    // Abrir en nueva ventana
    const printWindow = window.open('', '_blank');
    printWindow.document.write(tirillaHTML);
    printWindow.document.close();
  };

  const totalPerUnit = getTotalPerUnit();

  // Formatear total por unidad para mostrar
  const formatTotalPerUnit = () => {
    const { grams, ml } = totalPerUnit;

    // Si hay ambos (peso y volumen), mostrar en líneas separadas
    if (grams > 0 && ml > 0) {
      const weightStr = grams >= 1000 ? `${(grams / 1000).toFixed(3)} kg` : `${grams.toFixed(1)} g`;
      const volumeStr = ml >= 1000 ? `${(ml / 1000).toFixed(3)} l` : `${ml.toFixed(1)} ml`;
      return (
        <div style={{ lineHeight: '1.4' }}>
          <div>{weightStr} (peso)</div>
          <div>{volumeStr} (volumen)</div>
        </div>
      );
    }
    // Solo peso
    else if (grams > 0) {
      return grams >= 1000 ? `${(grams / 1000).toFixed(3)} kg` : `${grams.toFixed(1)} g`;
    }
    // Solo volumen
    else if (ml > 0) {
      return ml >= 1000 ? `${(ml / 1000).toFixed(3)} l` : `${ml.toFixed(1)} ml`;
    }
    return '-';
  };

  return (
    <div className="formulacion-form-simple">
      {/* Campo NOMBRE alineado con botón DUPLICAR */}
      <div className="form-group" style={{ marginBottom: '20px', position: 'relative' }}>
        <label className="form-label">NOMBRE:</label>
        <input
          type="text"
          className="form-input"
          value={formData.formula_name}
          onChange={(e) => handleInputChange('formula_name', e.target.value)}
          placeholder={
            formData.formula_type === 'EXCLUSIVA'
              ? 'Fórmula exclusiva - sin nombre'
              : 'Ingrese nombre de la fórmula'
          }
          disabled={formData.formula_type === 'EXCLUSIVA'}
        />

        {/* Mensaje de error para NOMBRE */}
        {validationErrors.formula_name && (
          <div style={{
            color: '#e74c3c',
            fontSize: '0.85em',
            marginTop: '5px',
            fontWeight: '500'
          }}>
            ⚠️ {validationErrors.formula_name}
          </div>
        )}
      </div>

      {/* Campos principales en dos columnas */}
      <div className="form-grid-2">
        {/* Columna Izquierda */}
        <div className="formulacion-column">
          <div className="form-group">
            <label className="form-label">COMPAÑÍA:</label>
            <select
              className="form-input"
              value={formData.company_name}
              onChange={(e) => handleInputChange('company_name', e.target.value)}
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            >
              {companyOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <div style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
              ℹ️ Se establece automáticamente según el tipo de producto
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">TIPO DE ENVASE:</label>
            <Autocomplete
              value={formData.container_product_id}
              onChange={(val) => handleInputChange('container_product_id', val)}
              onSearch={handleSearchPackaging}
              preloadedOptions={packagingMaterials}
              placeholder="Buscar material de empaque..."
              disabled={loadingPackaging}
            />
          </div>

          <div className="form-group">
            <label className="form-label">TAPADERA:</label>
            <Autocomplete
              value={formData.lid_product_id}
              onChange={(val) => handleInputChange('lid_product_id', val)}
              onSearch={handleSearchPackaging}
              preloadedOptions={packagingMaterials}
              placeholder="Buscar material de empaque..."
              disabled={loadingPackaging}
            />
          </div>

          <div className="form-group form-group-inline">
            <label className="form-label">CANTIDAD POR ENVASE:</label>
            <div className="input-group">
              <input
                type="number"
                className="form-input input-small"
                value={formData.quantity_per_container}
                onChange={(e) => handleInputChange('quantity_per_container', e.target.value)}
                placeholder="90"
              />
              <select
                className="form-input input-small"
                value={formData.quantity_unit}
                onChange={(e) => handleInputChange('quantity_unit', e.target.value)}
              >
                {quantityUnitOptions.map(unit => (
                  <option key={unit.value} value={unit.value}>{unit.label}</option>
                ))}
              </select>
            </div>
            {/* Mensaje de error para CANTIDAD POR ENVASE */}
            {validationErrors.quantity_per_container && (
              <div style={{
                color: '#e74c3c',
                fontSize: '0.85em',
                marginTop: '5px',
                fontWeight: '500'
              }}>
                ⚠️ {validationErrors.quantity_per_container}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">TIPO DE CÁPSULAS:</label>
            <select
              className="form-input"
              value={formData.capsule_type}
              onChange={(e) => handleInputChange('capsule_type', e.target.value)}
            >
              {capsuleTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {formData.capsule_type !== 'NOT_APPLICABLE' && (
            <div className="form-group">
              <label className="form-label">INGREDIENTE CÁPSULA:</label>
              <Autocomplete
                value={formData.capsule_ingredient_id}
                onChange={(val) => handleInputChange('capsule_ingredient_id', val)}
                onSearch={handleSearchCapsuleIngredient}
                preloadedOptions={availableIngredients}
                placeholder="Buscar ingrediente de cápsula..."
                disabled={loadingIngredients}
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">MEDIDA DE CAJA PARA EMPACAR:</label>
            <select
              className="form-input"
              value={formData.box_size}
              onChange={(e) => handleInputChange('box_size', e.target.value)}
            >
              {boxSizeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">PIEZAS POR CAJA:</label>
            <input
              type="number"
              className="form-input"
              value={formData.pieces_per_box}
              onChange={(e) => handleInputChange('pieces_per_box', e.target.value)}
              placeholder="105"
            />
          </div>

        </div>

        {/* Columna Derecha */}
        <div className="formulacion-column">
          <div className="form-group">
            <label className="form-label">FÓRMULA:</label>
            <select
              className="form-input"
              value={formData.formula_type}
              onChange={(e) => handleInputChange('formula_type', e.target.value)}
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            >
              {formulaTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <div style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
              ℹ️ Se establece automáticamente según el tipo de producto
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">TIEMPO PARA MEZCLAR (minutos):</label>
            <input
              type="number"
              className="form-input"
              value={formData.mixing_time_minutes}
              onChange={(e) => handleInputChange('mixing_time_minutes', e.target.value)}
              placeholder="20" min="1"
            />
          </div>

          <div className="form-group">
            <label className="form-label">COSTO MANO DE OBRA:</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontWeight: '600', color: '#555' }}>$</span>
              <input
                type="number"
                className="form-input"
                value={formData.labor_cost_per_hour}
                onChange={(e) => handleInputChange('labor_cost_per_hour', e.target.value)}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">DURACIÓN DE MEJOR USO (MESES):</label>
            <input
              type="number"
              className="form-input"
              placeholder="Ej: 12, 24, 36"
              min="1"
              value={formData._best_use_months || ''}
              onChange={(e) => {
                const months = e.target.value ? parseInt(e.target.value) : '';
                handleInputChange('_best_use_months', months);
                // Calcular fecha: hoy + N meses y guardar como ISO string
                if (months && months > 0) {
                  const date = new Date();
                  date.setMonth(date.getMonth() + months);
                  handleInputChange('best_use_date_years', date.toISOString().split('T')[0]);
                } else {
                  handleInputChange('best_use_date_years', '');
                }
              }}
            />
            {formData.best_use_date_years && (
              <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                Vence: {new Date(formData.best_use_date_years + 'T00:00:00').toLocaleDateString('es-GT', { year: 'numeric', month: 'long', day: 'numeric' })}
              </small>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">ETIQUETA DE IMPRESIÓN O ENVASE:</label>
            <select
              className="form-input"
              value={formData.label_printer}
              onChange={(e) => handleInputChange('label_printer', e.target.value)}
            >
              {labelPrinterOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {formData.label_printer !== 'NO_LABEL' && (
            <div className="form-group">
              <label className="form-label">MATERIAL DE ETIQUETA:</label>
              <Autocomplete
                value={formData.label_product_id}
                onChange={(val) => handleInputChange('label_product_id', val)}
                onSearch={handleSearchPackaging}
                preloadedOptions={packagingMaterials}
                placeholder="Buscar etiqueta en materiales de empaque..."
                disabled={loadingPackaging}
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">SILICA:</label>
            <select
              className="form-input"
              value={formData.includes_silica ? 'SI' : 'NO'}
              onChange={(e) => handleInputChange('includes_silica', e.target.value === 'SI')}
            >
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </select>
          </div>
        </div>
      </div>

      {/* Sección de Ingredientes */}
      <div className="form-section">
        <h3 className="section-title">INGREDIENTES</h3>

        {/* Panel para agregar ingrediente - siempre visible */}
        <div style={{
          border: '2px solid #7B2D8E',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          backgroundColor: '#fdf8ff',
        }}>
          <div style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#7B2D8E', fontSize: '14px' }}>Agregar Ingrediente</strong>
          </div>

            {/* Buscador */}
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <div style={{ position: 'relative' }}>
                <FiSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#999', pointerEvents: 'none' }} />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="form-input"
                  style={{ paddingLeft: '32px' }}
                  placeholder="Buscar ingrediente por nombre o código..."
                  value={addPanel.searchText}
                  onChange={(e) => setAddPanel(prev => ({ ...prev, searchText: e.target.value, selectedIngredient: prev.selectedIngredient?.name === e.target.value ? prev.selectedIngredient : null }))}
                  autoComplete="off"
                />
              </div>

              {/* Lista de sugerencias */}
              {addPanel.searchText && !addPanel.selectedIngredient && (() => {
                const matches = ingredientSearchResults;
                return searchingIngredients ? (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                    background: 'white', border: '1px solid #ddd', borderRadius: '4px',
                    padding: '10px 12px', color: '#999', fontSize: '13px'
                  }}>
                    Buscando...
                  </div>
                ) : matches.length > 0 ? (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 100,
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    maxHeight: '220px',
                    overflowY: 'auto',
                  }}>
                    {matches.map(item => {
                      const rawUnit = item.unit;
                      const unitMap = { KILOGRAM:'KG', GRAM:'G', MILLIGRAM:'MG', MICROGRAM:'MCG', LITER:'L', MILLILITER:'ML', UNIT:'UNIT' };
                      const displayUnit = rawUnit ? (unitMap[rawUnit] || rawUnit).toLowerCase() : '';
                      return (
                        <div
                          key={item.id}
                          style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                          onMouseDown={(e) => { e.preventDefault(); handleAddPanelSelectIngredient(item); }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5edff'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          <span style={{ fontWeight: '500' }}>{item.name}</span>
                          <span style={{ fontSize: '12px', color: '#888' }}>{item.code}{displayUnit ? ` · ${displayUnit}` : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                    background: 'white', border: '1px solid #ddd', borderRadius: '4px',
                    padding: '10px 12px', color: '#999', fontSize: '13px'
                  }}>
                    Sin resultados para "{addPanel.searchText}"
                  </div>
                );
              })()}
            </div>

            {/* Ingrediente seleccionado + cantidad */}
            {addPanel.selectedIngredient && (
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '160px' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Ingrediente</div>
                  <div style={{
                    padding: '8px 12px',
                    background: '#f0e6ff',
                    border: '1px solid #c89de8',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#5a1a7a',
                  }}>
                    {addPanel.selectedIngredient.name}
                    {(() => {
                      const rawUnit = addPanel.selectedIngredient.unit;
                      const unitMap = { KILOGRAM:'KG', GRAM:'G', MILLIGRAM:'MG', MICROGRAM:'MCG', LITER:'L', MILLILITER:'ML', UNIT:'UNIT' };
                      const displayUnit = rawUnit ? (unitMap[rawUnit] || rawUnit).toLowerCase() : null;
                      return displayUnit ? <span style={{ fontSize: '11px', color: '#888', marginLeft: '6px' }}>base: {displayUnit}</span> : null;
                    })()}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Cantidad</div>
                    <input
                      type="number"
                      className="form-input"
                      style={{ width: '100px' }}
                      placeholder="0.0"
                      step="any"
                      min="0"
                      value={addPanel.quantity}
                      onChange={(e) => setAddPanel(prev => ({ ...prev, quantity: e.target.value }))}
                      autoFocus
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddPanelConfirm(); }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Unidad</div>
                    <select
                      className="form-input"
                      style={{ width: '80px' }}
                      value={addPanel.unit}
                      onChange={(e) => setAddPanel(prev => ({ ...prev, unit: e.target.value }))}
                    >
                      {unitTypes.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                    </select>
                  </div>
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={handleAddPanelConfirm}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <FiPlus /> Agregar
                  </button>
                </div>
              </div>
            )}
        </div>

        {/* Tabla de ingredientes - SIEMPRE VISIBLE */}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{width: '170px'}} rowSpan="2">INGREDIENTES</th>
                <th style={{width: '140px'}} rowSpan="2">
                  {isDiscreteUnit() ? 'CANT. POR UNIDAD' : 'CANT. POR ENVASE'}
                  <br/>
                  <small style={{fontWeight: '600', fontSize: '12px', color: '#fff'}}>
                    {isDiscreteUnit()
                      ? (productCategory ? getCategoryDisplayName(productCategory) : 'CÁPSULA')
                      : `${formData.quantity_per_container || 0} ${(formData.quantity_unit || 'G').toLowerCase()}`
                    }
                  </small>
                </th>
                <th className="text-center" colSpan="4">CANTIDAD DE PRODUCCIÓN ({getQpcLabel()})</th>
                <th className="text-center" style={{width: '70px'}} rowSpan="2">ACCIONES</th>
              </tr>
              <tr>
                <th className="text-center" style={{width: '110px', padding: '5px'}}>
                  <input
                    type="number"
                    value={productionQuantities[0]}
                    onChange={(e) => {
                      const newQuantities = [...productionQuantities];
                      newQuantities[0] = parseInt(e.target.value) || 0;
                      setProductionQuantities(newQuantities);
                    }}
                    style={{
                      width: '90px',
                      padding: '4px',
                      textAlign: 'center',
                      border: '1px solid #ddd',
                      borderRadius: '3px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  />
                </th>
                <th className="text-center" style={{width: '110px', padding: '5px'}}>
                  <input
                    type="number"
                    value={productionQuantities[1]}
                    onChange={(e) => {
                      const newQuantities = [...productionQuantities];
                      newQuantities[1] = parseInt(e.target.value) || 0;
                      setProductionQuantities(newQuantities);
                    }}
                    style={{
                      width: '90px',
                      padding: '4px',
                      textAlign: 'center',
                      border: '1px solid #ddd',
                      borderRadius: '3px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  />
                </th>
                <th className="text-center" style={{width: '110px', padding: '5px'}}>
                  <input
                    type="number"
                    value={productionQuantities[2]}
                    onChange={(e) => {
                      const newQuantities = [...productionQuantities];
                      newQuantities[2] = parseInt(e.target.value) || 0;
                      setProductionQuantities(newQuantities);
                    }}
                    style={{
                      width: '90px',
                      padding: '4px',
                      textAlign: 'center',
                      border: '1px solid #ddd',
                      borderRadius: '3px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  />
                </th>
                <th className="text-center" style={{width: '110px', padding: '5px'}}>
                  <input
                    type="number"
                    value={productionQuantities[3]}
                    onChange={(e) => {
                      const newQuantities = [...productionQuantities];
                      newQuantities[3] = parseInt(e.target.value) || 0;
                      setProductionQuantities(newQuantities);
                    }}
                    style={{
                      width: '90px',
                      padding: '4px',
                      textAlign: 'center',
                      border: '1px solid #ddd',
                      borderRadius: '3px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {formData.ingredients.filter(i => !i.isNew).length > 0 ? (
                formData.ingredients.filter(i => !i.isNew).map((ing, index) => (
                  <tr key={index}>
                    {/* Columna: INGREDIENTES */}
                    <td className="ingredient-name" style={{ fontWeight: '500' }}>
                      {ing.ingredient_name}
                      {ing.base_unit && (
                        <div style={{ fontSize: '11px', color: '#999', fontWeight: 'normal' }}>
                          base: {ing.base_unit.toLowerCase()}
                        </div>
                      )}
                    </td>

                    {/* Columna: CANTIDAD DE INGREDIENTES */}
                    <td>
                      <div className="input-group-simple">
                        <input
                          type="number"
                          className="form-input"
                          value={ing.quantity_per_unit}
                          onChange={(e) => handleIngredientChange(index, 'quantity_per_unit', e.target.value)}
                          step="any"
                          placeholder="0.0"
                          style={{flex: 1}}
                        />
                        <select
                          className="form-input"
                          value={ing.unit}
                          onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
                          style={{width: '72px'}}
                        >
                          {unitTypes.map(unit => (
                            <option key={unit.value} value={unit.value}>{unit.label}</option>
                          ))}
                        </select>
                      </div>
                      {ing.base_unit && ing.base_unit !== ing.unit && (
                        <div style={{ fontSize: '10px', color: '#e67e22', marginTop: '2px', lineHeight: '1.2' }}>
                          ⚠ costo se convierte automáticamente
                        </div>
                      )}
                    </td>

                    {/* Columnas de producción */}
                    <td className="text-center production-cell">{getIngredientProductionAmount(ing, productionQuantities[0])}</td>
                    <td className="text-center production-cell">{getIngredientProductionAmount(ing, productionQuantities[1])}</td>
                    <td className="text-center production-cell">{getIngredientProductionAmount(ing, productionQuantities[2])}</td>
                    <td className="text-center production-cell">{getIngredientProductionAmount(ing, productionQuantities[3])}</td>

                    {/* Columna: ACCIONES */}
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn btn--ghost btn--small"
                        onClick={() => handleRemoveIngredient(index)}
                        title="Quitar ingrediente"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center" style={{padding: '2rem', color: '#999'}}>
                    No hay ingredientes agregados. Haga clic en "AGREGAR INGREDIENTE" para comenzar.
                  </td>
                </tr>
              )}
            </tbody>
            {formData.ingredients.filter(i => !i.isNew).length > 0 && (
              <tfoot>
                <tr className="total-row">
                  <td className="text-left"><strong>TOTAL INGREDIENTES:</strong></td>
                  <td className="text-center total-per-unit"><strong>{formatTotalPerUnit()}</strong></td>
                  <td className="text-center total-production"><strong>{getProductionAmount(productionQuantities[0])}</strong></td>
                  <td className="text-center total-production"><strong>{getProductionAmount(productionQuantities[1])}</strong></td>
                  <td className="text-center total-production"><strong>{getProductionAmount(productionQuantities[2])}</strong></td>
                  <td className="text-center total-production"><strong>{getProductionAmount(productionQuantities[3])}</strong></td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

      </div>
    </div>
  );
};

export default FormulacionFormSimple;
